import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@clari/ui'
import { TransactionList } from '@/components/banking/TransactionList'
import { TransactionListSkeleton } from '@/components/banking/skeletons'
import { bankingApi } from '@/lib/banking-api'

// A deliberately curated subset of the 21 ledger types (see @clari/shared
// TransactionTypeSchema) — a 21-entry dropdown helps nobody. Every value here
// must still BE in that schema: the API validates the query param against it, so
// a stale option is a 400. `internal_transfer` was exactly that after the type
// vocabulary was split into in/out/return.
const TX_TYPES = [
  'sepa_credit_transfer_in',
  'sepa_credit_transfer_out',
  'sepa_credit_transfer_return',
  'direct_debit_out',
  'card_payment',
  'card_refund',
  'fee',
  'other',
] as const
const TX_STATUSES = ['pending', 'booked', 'rejected', 'canceled'] as const

export function Transactions() {
  const { t, i18n } = useTranslation('banking')
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => bankingApi.listAccounts() })
  const account = accountsQuery.data?.items[0]

  const [type, setType] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)

  const q = useQuery({
    queryKey: ['tx', account?.id, type, status, page],
    queryFn: () =>
      bankingApi.listTransactions(account?.id ?? '', {
        ...(type ? { type } : {}),
        ...(status ? { status } : {}),
        page,
      }),
    enabled: Boolean(account),
  })
  const totalPages = q.data ? Math.max(1, Math.ceil(q.data.total / q.data.page_size)) : 1

  async function exportCsv() {
    if (!account) return
    const blob = await bankingApi.exportTransactions(account.id)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'transactions.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('transactions.title')}</h1>
        <Button variant="secondary" size="sm" onClick={exportCsv} disabled={!account}>
          {t('transactions.export')}
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <select
          className="clari-input max-w-xs"
          value={type}
          onChange={(e) => {
            setType(e.target.value)
            setPage(1)
          }}
          aria-label={t('transactions.filter_type')}
        >
          <option value="">{t('transactions.all')}</option>
          {TX_TYPES.map((ty) => (
            <option key={ty} value={ty}>
              {t(`transactions.type.${ty}`)}
            </option>
          ))}
        </select>
        <select
          className="clari-input max-w-xs"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            setPage(1)
          }}
          aria-label={t('transactions.filter_status')}
        >
          <option value="">{t('transactions.all')}</option>
          {TX_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`transactions.status_${s}`)}
            </option>
          ))}
        </select>
      </div>

      {q.isLoading ? <TransactionListSkeleton rows={8} /> : null}
      {q.data ? <TransactionList items={q.data.items} locale={i18n.language} /> : null}

      <div className="flex items-center gap-3 text-sm">
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          {t('transfer.back')}
        </button>
        <span className="text-text-muted">{page}</span>
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          {t('transfer.continue')}
        </button>
      </div>
    </div>
  )
}
